/**
 * collection.js — Faceted filters + sort (Section Rendering API) + mobile filter drawer
 */

function getSectionInstanceId(element) {
  const wrapper = element?.closest?.('.shopify-section');
  if (!wrapper?.id) return null;
  return wrapper.id.replace(/^shopify-section-/, '');
}

function isDesktopFilters() {
  return window.matchMedia('(min-width: 1024px)').matches;
}

/**
 * Mobile: open/close filter drawer. Desktop: no-op (sidebar always visible).
 */
function setCollectionFiltersOpen(open) {
  const overlay = document.getElementById('collection-filters-overlay');
  const panel = document.getElementById('collection-filters-panel');
  const btn = document.querySelector('[data-open-filters]');
  if (!overlay || !panel) return;

  overlay.classList.toggle('is-open', open);
  panel.classList.toggle('is-open', open);
  overlay.setAttribute('aria-hidden', (!open).toString());
  panel.setAttribute('aria-hidden', (!open).toString());
  btn?.setAttribute('aria-expanded', open ? 'true' : 'false');
  document.body.style.overflow = open ? 'hidden' : '';
}

function syncFiltersDrawerAria() {
  const overlay = document.getElementById('collection-filters-overlay');
  const panel = document.getElementById('collection-filters-panel');
  if (!overlay || !panel) return;

  const open = panel.classList.contains('is-open');
  panel.setAttribute('aria-hidden', open ? 'false' : 'true');
  overlay.setAttribute('aria-hidden', open ? 'false' : 'true');
}

function syncFilterActiveState() {
  const form = document.getElementById('FacetFiltersForm');
  if (!form) return;
  const count = parseInt(form.getAttribute('data-active-count') || '0', 10);

  const refineBtn = document.querySelector('[data-open-filters]');
  if (refineBtn) {
    refineBtn.setAttribute('data-active-count', String(count));
    const countEl = refineBtn.querySelector('.refine-button__count');
    if (countEl) countEl.textContent = String(count);
  }

  const footerReset = document.querySelector('.collection-filters__footer-reset');
  if (footerReset) {
    footerReset.style.display = count > 0 ? '' : 'none';
  }
}

function initCollectionFiltersDrawer() {
  syncFiltersDrawerAria();

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-filters]')) {
      e.preventDefault();
      setCollectionFiltersOpen(true);
    }
    if (e.target.closest('[data-close-filters]')) {
      setCollectionFiltersOpen(false);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const panel = document.getElementById('collection-filters-panel');
    if (panel?.classList.contains('is-open')) {
      setCollectionFiltersOpen(false);
    }
  });

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(syncFiltersDrawerAria, 100);
  });
}

class FacetFilters {
  constructor() {
    this.grid = document.getElementById('CollectionProductGrid');
    this.form = document.getElementById('FacetFiltersForm');

    if (!this.grid) return;

    this.mainSectionId = getSectionInstanceId(this.grid);

    if (!this.mainSectionId) return;

    this.debouncedFilter = debounce(this.applyFilters.bind(this), 400);

    if (this.form) {
      this.form.addEventListener('change', this.debouncedFilter);
    }

    const sortSelect = document.querySelector('[data-sort-by]');
    if (sortSelect) {
      sortSelect.addEventListener('change', () => this.applyFilters());
    }

    window.addEventListener('popstate', () => this.applyFilters(false));
  }

  async applyFilters(updateHistory = true) {
    const params = this.form
      ? new URLSearchParams(new FormData(this.form))
      : new URLSearchParams(window.location.search);

    const sortSelect = document.querySelector('[data-sort-by]');
    if (sortSelect) {
      params.set('sort_by', sortSelect.value);
    }
    const query = params.toString();
    const newUrl = query
      ? `${window.location.pathname}?${query}`
      : window.location.pathname;

    if (updateHistory) {
      history.pushState({ path: newUrl }, '', newUrl);
    }

    this.grid.setAttribute('aria-busy', 'true');

    try {
      const data = await fetchSection(newUrl, [this.mainSectionId]);

      const mainHtml = data[this.mainSectionId];
      if (mainHtml) {
        renderSection(
          '#CollectionProductGrid',
          mainHtml,
          '#CollectionProductGrid'
        );

        const filterForm = document.getElementById('FacetFiltersForm');
        if (filterForm) {
          renderSection(
            '#FacetFiltersForm',
            mainHtml,
            '#FacetFiltersForm'
          );

          // Re-bind form listener and re-initialize price range sliders after DOM replacement
          this.form = document.getElementById('FacetFiltersForm');
          if (this.form) {
            this.form.addEventListener('change', this.debouncedFilter);
          }
          if (typeof initPriceRangeSliders === 'function') {
            initPriceRangeSliders();
          }

          // Sync refine button + drawer footer reset state with new active count
          syncFilterActiveState();
        }
      }
    } catch (err) {
      console.error('Filter update failed:', err);
    }

    this.grid.removeAttribute('aria-busy');

    // Notify infinite scroll (and other listeners) that the grid was replaced
    document.dispatchEvent(new CustomEvent('collection:updated'));

    if (window.innerWidth < 768) {
      this.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initCollectionFiltersDrawer();
  new FacetFilters();
});
